import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useClubAttention } from "./ClubAttentionContext";

const ClubMemberUnreadContext = createContext(null);

const clampInt = (value) => {
  const next = Number(value);
  if (!Number.isFinite(next)) return 0;
  return Math.max(0, Math.floor(next));
};

const normalizeMeta = (rawMeta) => {
  if (rawMeta && typeof rawMeta === "object") return rawMeta;
  if (typeof rawMeta !== "string") return {};
  try {
    const parsed = JSON.parse(rawMeta);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const EMPTY_CONTEXT = {
  hasLoaded: false,
  totalUnread: 0,
  unreadClubsCount: 0,
  byClubId: {},
  joinedTotalUnread: 0,
  joinedUnreadClubsCount: 0,
  joinedByClubId: {},
  refetch: async () => {},
};

export const ClubMemberUnreadProvider = ({ children }) => {
  const { data: attentionData, refetch } = useClubAttention();
  const data = attentionData?.member_activity;

  useEffect(() => {
    const handleNotification = (event) => {
      const meta = normalizeMeta(event?.detail?.meta);
      const actionKind = String(meta?.action_kind || meta?.actionKind || "").trim().toLowerCase();
      if (actionKind === "club_join_request_approved") void refetch();
    };

    window.addEventListener("vg:notification", handleNotification);
    return () => window.removeEventListener("vg:notification", handleNotification);
  }, [refetch]);

  const value = useMemo(() => {
    const byClubId = {};
    const clubs = Array.isArray(data?.clubs) ? data.clubs : [];
    clubs.forEach((item) => {
      const clubId = item?.club_id != null ? String(item.club_id) : null;
      if (!clubId) return;
      byClubId[clubId] = clampInt(item?.unread_count);
    });

    const joinedByClubId = {};
    const joinedClubs = Array.isArray(data?.joined_clubs) ? data.joined_clubs : [];
    joinedClubs.forEach((item) => {
      const clubId = item?.club_id != null ? String(item.club_id) : null;
      if (!clubId) return;
      joinedByClubId[clubId] = clampInt(item?.unread_count || 1);
    });

    return {
      hasLoaded: data !== undefined,
      totalUnread: clampInt(data?.total_unread),
      unreadClubsCount: clampInt(data?.unread_clubs_count),
      byClubId,
      joinedTotalUnread: clampInt(data?.joined_total_unread),
      joinedUnreadClubsCount: clampInt(data?.joined_unread_clubs_count),
      joinedByClubId,
      refetch,
    };
  }, [data, refetch]);

  return (
    <ClubMemberUnreadContext.Provider value={value}>
      {children}
    </ClubMemberUnreadContext.Provider>
  );
};

export const useClubMemberUnread = () => {
  const ctx = useContext(ClubMemberUnreadContext);
  return ctx || EMPTY_CONTEXT;
};
